import React, { useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import { formatDate } from "helpers/misc_helpers"
import {
  SelectTraderOffer,
  SendTraderRequestMessage,
  UpdateTraderRequestStatus,
} from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"
import TraderRequestChat from "./TraderRequestChat"

const statusColors = {
  open: "success",
  evaluating: "warning",
  in_progress: "primary",
  completed: "secondary",
  cancelled: "danger",
}

const statusLabels = {
  open: "Open",
  evaluating: "Evaluating",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
}

const OEMRequestView = ({ request, offers, onRefresh }) => {
  const setGlobalLoading = useLoading()
  const [confirmOffer, setConfirmOffer] = useState(null)
  const [confirmClose, setConfirmClose] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  // { [offerId]: Set<partId> } — tracks which part lines are checked per trader offer
  const [selectedLines, setSelectedLines] = useState({})

  const toggleLine = (offerId, partId) => {
    setSelectedLines(prev => {
      const current = new Set(prev[offerId] || [])
      if (current.has(partId)) current.delete(partId)
      else current.add(partId)
      return { ...prev, [offerId]: new Set(current) }
    })
  }

  const toggleAllLines = (offerId, partIds) => {
    setSelectedLines(prev => {
      const current = prev[offerId] || new Set()
      const allSelected = partIds.every(id => current.has(id))
      return { ...prev, [offerId]: allSelected ? new Set() : new Set(partIds) }
    })
  }

  const getSelectedCount = offerId => selectedLines[offerId]?.size || 0

  const handleSelectOffer = async () => {
    if (!confirmOffer) return
    setGlobalLoading(true)
    setError("")
    try {
      const lineIds = Array.from(selectedLines[confirmOffer.id] || [])
      await SelectTraderOffer(
        request.id,
        confirmOffer.id,
        lineIds.length > 0 ? lineIds : null
      )
      await SendTraderRequestMessage(request.id, {
        message: `✓ offer confirmed — ${confirmOffer.traderCompanyName}`,
      })
      setConfirmOffer(null)
      setSelectedLines({})
      onRefresh()
    } catch (err) {
      console.error(err)
      setError("Failed to select offer. Please try again.")
    }
    setGlobalLoading(false)
  }

  const handleCancelRequest = async () => {
    setGlobalLoading(true)
    setError("")
    setSuccess("")
    try {
      await UpdateTraderRequestStatus(request.id, "cancelled")
      setSuccess("Request cancelled.")
      onRefresh()
    } catch (err) {
      console.error(err)
      setError("Failed to cancel request. Please try again.")
    }
    setGlobalLoading(false)
  }

  const handleCloseRequest = async () => {
    setGlobalLoading(true)
    try {
      await UpdateTraderRequestStatus(request.id, "completed")
      setConfirmClose(false)
      onRefresh()
    } catch (err) {
      console.error(err)
    }
    setGlobalLoading(false)
  }

  const isAnonymous = !["in_progress", "completed"].includes(request.status)
  const canSelect = request.status === "open" || request.status === "evaluating"
  const selectedOffer = (offers || []).find(o => o.status === "selected")

  // Parts selected for the offer currently pending confirmation
  const confirmSelectedParts = confirmOffer
    ? (request.parts || []).filter(p =>
        (selectedLines[confirmOffer.id] || new Set()).has(p.id)
      )
    : []

  // ── Per-line: Grid-Tabelle (Zeilen = Parts, Spalten = Trader-Angebote) ──────
  // `offers` ist hier die (gefilterte) Liste der per-Zeile-Angebote.
  const renderPerLineGrid = offers => {
    if (!offers || offers.length === 0) {
      return <p className="text-muted">No offers submitted yet.</p>
    }
    const partIds = (request.parts || []).map(p => p.id)
    return (
      <>
        <div className="mb-2 text-muted" style={{ fontSize: 12 }}>
          Prices shown are <strong>price per piece</strong> unless a trader
          uploaded a bulk offer.
        </div>
        {canSelect && (
          <div className="mb-2 text-muted" style={{ fontSize: 12 }}>
            <i className="bx bx-info-circle me-1"></i>
            Check lines per trader, then click <strong>Select Trader</strong> to
            place a grouped order.
          </div>
        )}
        <div className="table-responsive">
          <Table className="table table-bordered table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>Part Number</th>
                <th>Manufacturer</th>
                <th>Qty</th>
                <th>Datecode</th>
                {offers.map(offer => {
                  const selSet = selectedLines[offer.id] || new Set()
                  const allSelected =
                    partIds.length > 0 && partIds.every(id => selSet.has(id))
                  const someSelected = partIds.some(id => selSet.has(id))
                  return (
                    <th key={offer.id}>
                      <div className="d-flex align-items-center gap-1 flex-wrap">
                        {canSelect && (
                          <input
                            type="checkbox"
                            className="form-check-input mt-0 me-1"
                            checked={allSelected}
                            ref={el => {
                              if (el)
                                el.indeterminate = someSelected && !allSelected
                            }}
                            onChange={() => toggleAllLines(offer.id, partIds)}
                            title="Select / deselect all lines for this trader"
                            style={{ cursor: "pointer" }}
                          />
                        )}
                        <span>{offer.traderCompanyName}</span>
                        {offer.status === "selected" && (
                          <Badge color="success" className="ms-1">
                            Selected
                          </Badge>
                        )}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {request.parts.map(part => (
                <tr key={part.id}>
                  <td>{part.partNumber}</td>
                  <td>{part.manufacturer}</td>
                  <td>{part.quantity}</td>
                  <td>{part.datecode || "—"}</td>
                  {offers.map(offer => {
                    const line = (offer.lines || []).find(
                      l => l.traderRequestPartID === part.id
                    )
                    const isChecked = (
                      selectedLines[offer.id] || new Set()
                    ).has(part.id)
                    return (
                      <td
                        key={offer.id}
                        className={
                          canSelect && isChecked ? "table-success" : ""
                        }
                        style={{ cursor: canSelect ? "pointer" : "default" }}
                        onClick={() =>
                          canSelect && toggleLine(offer.id, part.id)
                        }
                      >
                        <div className="d-flex align-items-center gap-1">
                          {canSelect && (
                            <input
                              type="checkbox"
                              className="form-check-input mt-0 me-1"
                              checked={isChecked}
                              onChange={() => toggleLine(offer.id, part.id)}
                              onClick={e => e.stopPropagation()}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                          {line?.price != null
                            ? `${parseFloat(line.price).toLocaleString(
                                "de-DE",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                }
                              )} ${line.currency || "EUR"}`
                            : "—"}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
              {/* Totals row — shows selected-lines subtotal when any lines are checked */}
              <tr className="table-light fw-bold">
                <td colSpan={4}>
                  {canSelect && offers.some(o => getSelectedCount(o.id) > 0)
                    ? "Selected Total"
                    : "Total"}
                </td>
                {offers.map(offer => {
                  const selSet = selectedLines[offer.id]
                  const linesToSum =
                    canSelect && selSet?.size > 0
                      ? (offer.lines || []).filter(l =>
                          selSet.has(l.traderRequestPartID)
                        )
                      : offer.lines || []
                  const total = linesToSum.reduce((sum, l) => {
                    const part = (request.parts || []).find(
                      p => p.id === l.traderRequestPartID
                    )
                    return sum + (l.price || 0) * (part?.quantity || 0)
                  }, 0)
                  return (
                    <td key={offer.id}>
                      {total > 0
                        ? `${total.toLocaleString("de-DE", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4,
                          })} ${offer.currency || "EUR"}`
                        : "—"}
                    </td>
                  )
                })}
              </tr>
            </tbody>
          </Table>

          {/* Select-Trader buttons — enabled only when lines are checked for that trader */}
          {canSelect && (
            <div className="d-flex gap-2 mt-2 flex-wrap">
              {offers.map(offer => {
                const count = getSelectedCount(offer.id)
                return (
                  <Button
                    key={offer.id}
                    color="success"
                    size="sm"
                    outline={count === 0}
                    disabled={count === 0}
                    onClick={() => setConfirmOffer(offer)}
                  >
                    Select Trader
                    {count > 0 && (
                      <Badge
                        color="light"
                        className="ms-2 text-success border border-success"
                      >
                        {count} line{count !== 1 ? "s" : ""}
                      </Badge>
                    )}
                  </Button>
                )
              })}
            </div>
          )}
        </div>
      </>
    )
  }

  // ── Bulk: Liste der Gesamtangebote ───────────────────────────────────────
  // `offers` ist hier die (gefilterte) Liste der Bulk-Angebote.
  const renderBulkOffers = offers => {
    if (!offers || offers.length === 0) {
      return <p className="text-muted">No offers submitted yet.</p>
    }
    return (
      <>
        <div className="mb-2 text-muted" style={{ fontSize: 12 }}>
          These traders submitted a <strong>bulk total price</strong> for all
          parts.
        </div>
        <div className="table-responsive">
          <Table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Trader</th>
                <th>Total Price</th>
                <th>Currency</th>
                <th>Notes</th>
                <th>Submitted</th>
                {canSelect && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {offers.map(offer => (
                <tr key={offer.id}>
                  <td>
                    {offer.traderCompanyName}
                    {offer.status === "selected" && (
                      <Badge color="success" className="ms-1">
                        Selected
                      </Badge>
                    )}
                  </td>
                  <td>
                    {offer.totalPrice != null
                      ? parseFloat(offer.totalPrice).toLocaleString("de-DE", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4,
                        })
                      : "—"}
                  </td>
                  <td>{offer.currency || "EUR"}</td>
                  <td>{offer.notes || "—"}</td>
                  <td>{formatDate(offer.dateCreated)}</td>
                  {canSelect && (
                    <td>
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => setConfirmOffer(offer)}
                      >
                        Select
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </>
    )
  }

  return (
    <>
      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" className="mb-3">
          {success}
        </Alert>
      )}

      {/* Request Header */}
      <Card className="mb-3">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">{request.title}</h5>
              <small className="text-muted">
                Created: {formatDate(request.dateCreated)}
              </small>
            </Col>
            <Col xs="auto" className="d-flex gap-2 align-items-center">
              <Badge
                color={statusColors[request.status] || "secondary"}
                className="fs-6"
              >
                {statusLabels[request.status] || request.status}
              </Badge>
              {canSelect && (
                <Button
                  color="danger"
                  size="sm"
                  outline
                  onClick={handleCancelRequest}
                >
                  Cancel Request
                </Button>
              )}
              {request.status === "in_progress" && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => setConfirmClose(true)}
                >
                  <i className="bx bx-check me-1"></i>
                  Close Request
                </Button>
              )}
            </Col>
          </Row>
          {request.notes && (
            <p className="text-muted mt-2 mb-0">{request.notes}</p>
          )}
          {isAnonymous && (
            <Alert color="info" className="mt-3 mb-0 py-2">
              <i className="bx bx-lock me-1"></i>
              Traders are shown <strong>by name</strong>. Your identity stays
              anonymous to them until you select an offer.
            </Alert>
          )}
          {!isAnonymous && (
            <Alert color="success" className="mt-3 mb-0 py-2">
              <i className="bx bx-check-circle me-1"></i>
              Offer selected — your identity has been revealed to the selected
              trader. You can proceed with negotiations.
            </Alert>
          )}
        </CardBody>
      </Card>

      {/* Order Acceptance Confirmation — shown once a trader offer has been selected */}
      {selectedOffer && (
        <Card className="mb-3 border-success">
          <CardBody className="py-3">
            <div className="d-flex align-items-start gap-3">
              <i
                className="bx bx-check-circle text-success mt-1"
                style={{ fontSize: 22 }}
              ></i>
              <div className="flex-grow-1">
                <div className="fw-bold mb-1">
                  Order accepted &mdash; {selectedOffer.traderCompanyName}
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  <strong>Business Partner:</strong>{" "}
                  {selectedOffer.traderCompanyName}
                </div>
                {(() => {
                  const orderedLines = (selectedOffer.lines || []).filter(
                    l => l.isSelected === 1 || l.isSelected === true
                  )
                  if (orderedLines.length === 0) return null
                  return (
                    <div className="mt-2">
                      <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                        {orderedLines.length} line
                        {orderedLines.length !== 1 ? "s" : ""} ordered
                      </div>
                      <ListGroup flush>
                        {orderedLines.map(line => {
                          const part = (request.parts || []).find(
                            p => p.id === line.traderRequestPartID
                          )
                          return (
                            <ListGroupItem
                              key={line.id || line.traderRequestPartID}
                              className="px-0 py-1 border-0"
                            >
                              <div
                                className="d-flex justify-content-between align-items-center"
                                style={{ fontSize: 13 }}
                              >
                                <span className="text-dark">
                                  {part?.partNumber || "—"}
                                  <span className="text-muted ms-2">
                                    × {part?.quantity || "?"}
                                  </span>
                                </span>
                                <span className="text-muted">
                                  {line.price != null
                                    ? `${(
                                        parseFloat(line.price) *
                                        (part?.quantity || 1)
                                      ).toLocaleString("de-DE", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 4,
                                      })} ${line.currency || "EUR"}`
                                    : "—"}
                                </span>
                              </div>
                            </ListGroupItem>
                          )
                        })}
                      </ListGroup>
                    </div>
                  )
                })()}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Parts Table */}
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">
            <i className="bx bx-list-ul me-1"></i>
            Parts ({request.parts?.length || 0})
          </h6>
          <div className="table-responsive">
            <Table className="table table-sm table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Part Number</th>
                  <th>Manufacturer</th>
                  <th>Quantity</th>
                  <th>Datecode</th>
                  <th>Internal Part</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {(request.parts || []).map(part => (
                  <tr key={part.id}>
                    <td>{part.partNumber}</td>
                    <td>{part.manufacturer}</td>
                    <td>{part.quantity}</td>
                    <td>{part.datecode || "—"}</td>
                    <td>{part.internalPart || "—"}</td>
                    <td>{part.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* Offers */}
      <Card>
        <CardBody>
          <h6 className="mb-3">
            <i className="bx bx-dollar me-1"></i>
            Offers ({offers?.length || 0})
          </h6>
          {(() => {
            const all = offers || []
            if (all.length === 0) {
              return <p className="text-muted">No offers submitted yet.</p>
            }
            // Jeder Trader entscheidet je Angebot: pro Zeile oder Gesamtpreis.
            const perLine = all.filter(o =>
              (o.lines || []).some(l => l.price != null)
            )
            const bulk = all.filter(
              o => !(o.lines || []).some(l => l.price != null)
            )
            return (
              <>
                {perLine.length > 0 && renderPerLineGrid(perLine)}
                {bulk.length > 0 && (
                  <div className={perLine.length > 0 ? "mt-4" : ""}>
                    {perLine.length > 0 && (
                      <h6 className="mb-2">
                        <i className="bx bx-package me-1"></i>Bulk offers
                      </h6>
                    )}
                    {renderBulkOffers(bulk)}
                  </div>
                )}
              </>
            )
          })()}
        </CardBody>
      </Card>

      {/* Chat nach Offer-Annahme */}
      {["in_progress", "completed"].includes(request.status) && (
        <TraderRequestChat requestId={request.id} />
      )}

      {/* Close Request Modal */}
      <Modal isOpen={confirmClose} toggle={() => setConfirmClose(false)}>
        <ModalHeader toggle={() => setConfirmClose(false)}>
          Close Request
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to close this request?</p>
          <Alert color="info" className="mb-0">
            <i className="bx bx-info-circle me-1"></i>
            The request will be marked as <strong>Completed</strong> and no
            further changes can be made.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setConfirmClose(false)}
          >
            Cancel
          </Button>
          <Button color="success" onClick={handleCloseRequest}>
            <i className="bx bx-check me-1"></i>
            Confirm & Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirm-Modal — shows grouped selected lines for the chosen trader */}
      <Modal isOpen={!!confirmOffer} toggle={() => setConfirmOffer(null)}>
        <ModalHeader toggle={() => setConfirmOffer(null)}>
          Select Trader — {confirmOffer?.traderCompanyName}
        </ModalHeader>
        <ModalBody>
          <p>
            Confirm order from{" "}
            <strong>{confirmOffer?.traderCompanyName}</strong>?
          </p>

          {/* Grouped selected lines */}
          {confirmSelectedParts.length > 0 && (
            <div className="mb-3">
              <div className="text-muted mb-1" style={{ fontSize: 13 }}>
                <strong>
                  {confirmSelectedParts.length} line
                  {confirmSelectedParts.length !== 1 ? "s" : ""}
                </strong>{" "}
                selected:
              </div>
              <ListGroup flush className="border rounded">
                {confirmSelectedParts.map(part => {
                  const line = (confirmOffer?.lines || []).find(
                    l => l.traderRequestPartID === part.id
                  )
                  const lineTotal =
                    line?.price != null
                      ? parseFloat(line.price) * (part.quantity || 1)
                      : null
                  return (
                    <ListGroupItem key={part.id} className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="fw-medium">{part.partNumber}</span>
                          <span
                            className="text-muted ms-2"
                            style={{ fontSize: 12 }}
                          >
                            × {part.quantity}
                          </span>
                        </div>
                        <span className="text-muted" style={{ fontSize: 13 }}>
                          {lineTotal != null
                            ? `${lineTotal.toLocaleString("de-DE", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })} ${line.currency || "EUR"}`
                            : "—"}
                        </span>
                      </div>
                    </ListGroupItem>
                  )
                })}
                {/* Subtotal */}
                {(() => {
                  const subtotal = confirmSelectedParts.reduce((sum, part) => {
                    const line = (confirmOffer?.lines || []).find(
                      l => l.traderRequestPartID === part.id
                    )
                    return sum + (line?.price || 0) * (part.quantity || 1)
                  }, 0)
                  return subtotal > 0 ? (
                    <ListGroupItem className="py-2 fw-bold bg-light">
                      <div className="d-flex justify-content-between">
                        <span>Order Total</span>
                        <span>
                          {subtotal.toLocaleString("de-DE", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4,
                          })}{" "}
                          {confirmOffer?.currency || "EUR"}
                        </span>
                      </div>
                    </ListGroupItem>
                  ) : null
                })()}
              </ListGroup>
            </div>
          )}

          <Alert color="warning" className="mb-0">
            <i className="bx bx-info-circle me-1"></i>
            After selecting, your company identity will be revealed to the
            trader and the request status will change to{" "}
            <strong>In Progress</strong>.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setConfirmOffer(null)}
          >
            Cancel
          </Button>
          <Button color="success" onClick={handleSelectOffer}>
            Confirm Selection
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default OEMRequestView
